import config from 'services/config'
import store from 'main/store'
import {
  checkIfSessionExists,
  handleUnsuccesfulResponse
} from 'services/helpers/authorizationHandlers'
import LocalUserService from 'services/localUserService'
import { reportException } from 'utils/sentryReporter'

export async function responseHandler(api) {
  const response = await api()

  if (!response) {
    throw new Error('error during fetch')
  }

  const res = Object.prototype.hasOwnProperty.call(response, 'isSuccess')
    ? response
    : response.data

  const resSuccess = res?.isSuccess ?? res?.IsSuccess
  if (resSuccess === false) {
    throw new Error(res.message ?? res.Message)
  }

  if (resSuccess) {
    return res.result ?? res.Result
  }

  if (res?.message === 'SessionTimedOut') {
    console.warn('SessionTimedOut')
  }
}

const baseUrl = config.apiUrl + '/'

const userService = new LocalUserService()

export function getDirectfulUploadHeaders(url = '', extraHeaders = {}) {
  const userInfo = userService.getCurrentUserInfo()
  const currentHotelId = store.getState().login?.currentHotelId
  const isInternalAdmin = store.getState().login?.isInternalAdmin
  const tenantHeaders =
    currentHotelId && (isInternalAdmin || url?.includes('llm-platform/'))
      ? { 'sp-merchant-auto-admin-subsidiary-id': currentHotelId }
      : null

  return {
    'Cache-Control': 'no-cache',
    ...(userInfo?.sessionId ? { 'sp-auth': userInfo.sessionId } : null),
    ...tenantHeaders,
    ...extraHeaders
  }
}

async function fetchHandler(
  method,
  url = ``,
  body,
  abortController,
  withSessionId = true,
  dataType
) {
  let signal = null

  if (abortController) {
    signal = abortController.signal
  }

  checkIfSessionExists()

  const getHeaders = () => {
    const userInfo = userService.getCurrentUserInfo()
    const currentHotelId = store.getState().login?.currentHotelId
    const isInternalAdmin = store.getState().login?.isInternalAdmin
    const tenantHeaders =
      currentHotelId && (isInternalAdmin || url?.includes('llm-platform/'))
        ? { 'sp-merchant-auto-admin-subsidiary-id': currentHotelId }
        : null
    const actorHeaders =
      url?.includes('llm-platform/') && userInfo?.dashboardUserId
        ? {
            'sp-merchant-auto-admin-id': String(userInfo.dashboardUserId),
            ...(userInfo?.user?.email
              ? { 'x-mcp-actor-email': userInfo.user.email }
              : null),
            ...(userInfo?.user?.name
              ? { 'x-directful-user-name': userInfo.user.name }
              : null)
          }
        : null

    return {
      'Content-Type':
        body instanceof FormData ? 'multipart/form-data' : 'application/json',
      'Cache-Control': 'no-cache',
      ...(withSessionId ? { 'sp-auth': userInfo?.sessionId } : null),
      ...tenantHeaders,
      ...actorHeaders
    }
  }

  const readData = (dataType, data) => {
    if (dataType === 'text') {
      return data.text()
    } else if (dataType === 'blob') {
      return data.blob()
    } else if (dataType === 'json') {
      return data.json()
    } else {
      return data
    }
  }

  const isOptionalAssistLlmRequest =
    url?.includes('llm-platform/v1/assist/tasks') ||
    url?.includes('llm-platform/v1/assist/drip-proposal')

  return fetch(baseUrl + url, {
    method: method,
    headers: getHeaders(),
    body: body instanceof FormData ? body : body ? JSON.stringify(body) : null,
    signal
  })
    .then(async res => {
      const data = await readData(dataType, res)

      if (dataType !== 'json') {
        if (!res.ok) {
          const err = new Error(res.statusText || 'Request failed')
          err.status = res.status
          throw err
        }
        return data
      }

      // LLM Platform endpoints return data directly without isSuccess wrapper
      if (url?.includes('llm-platform/')) {
        if (!res.ok) {
          const errorMessage = data?.detail || data?.error || data?.message || res.statusText || 'Request failed'
          const err = new Error(`LLM Platform service error: ${errorMessage}`)
          err.status = res.status
          err.data = data
          throw err
        }

        if (data?.error || data?.detail) {
          const errorMessage = data?.detail || data?.error || 'Unknown error'
          const userMessage = data?.message || 'LLM Platform service error'
          throw new Error(`${userMessage}: ${errorMessage}`)
        }

        return data
      }

      const success = data?.isSuccess ?? data?.IsSuccess
      const payload = data?.result ?? data?.Result
      if (success) {
        return payload
      } else if (success === false) {
        // Fail-safe: do not run global handler for logs endpoint failure (avoids recursion / loop)
        const isLogsEndpoint = url === 'logs' || url.endsWith('/logs')
        if (isLogsEndpoint) {
          throw new Error(data?.message || data?.Message || 'Logs request failed')
        }
        handleUnsuccesfulResponse(data, url)
      }
    })
    .catch(error => {
      const isLogsEndpoint = url === 'logs' || url?.endsWith('/logs')
      if (isLogsEndpoint) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('Logging request failed:', error.message)
        }
        return undefined
      }

      const isOptionalAssistNotFound = isOptionalAssistLlmRequest && error?.status === 404

      if (!isOptionalAssistNotFound) {
        reportException(error)
      }

      throw error
    })
}

export const fetcher = {
  get: (
    url,
    queryParams = {},
    abortController,
    isSessionExist = true,
    dataType = 'json'
  ) => {
    const cleanParams = JSON.parse(JSON.stringify(queryParams))

    return fetchHandler(
      'GET',
      url +
        (Object.keys(cleanParams).length === 0
          ? ''
          : `?${new URLSearchParams(cleanParams)}`),
      null,
      abortController,
      isSessionExist,
      dataType
    )
  },
  post: (url, body, abortController, isSessionExist = true, dataType = 'json') =>
    fetchHandler('POST', url, body, abortController, isSessionExist, dataType),
  put: (url, body, abortController, isSessionExist = true, dataType = 'json') =>
    fetchHandler('PUT', url, body, abortController, isSessionExist, dataType),
  del: (url, body, abortController, isSessionExist = true, dataType = 'json') =>
    fetchHandler('DELETE', url, body, abortController, isSessionExist, dataType),
  patch: (url, body, abortController, isSessionExist = true, dataType = 'json') =>
    fetchHandler('PATCH', url, body, abortController, isSessionExist, dataType)
}
